import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

// ----------------------------------------------------------------------

const initialState = {
    isLoading: false,
    error: null,

    // UI State
    ui: {
        activeConversationId: null,
        isPanelOpen: true,
        globalUnreadCount: 0,
        soundEnabled: true,
    },

    // Conversations (Normalized)
    conversations: {
        byId: {},
        allIds: [],
        unreadByConversationId: {},
    },

    // Messages (Normalized)
    messages: {
        byId: {},
        idsByConversationId: {}, // conversationId -> [messageId]
        byClientId: {}, // clientId -> messageId (for optimistic updates)
    },

    // Presence
    presence: {
        userStatusById: {}, // { [userId]: { online: boolean, lastSeenAt: date } }
    },

    // WebRTC Call State
    call: {
        status: 'idle', // 'idle' | 'calling' | 'ringing' | 'active'
        isIncoming: false,
        partner: null, // { _id, name, avatar }
        chatId: null,
        type: 'audio', // 'audio' | 'video'
        participantIds: [], // For group calls
    },

    // Call Waiting State
    secondaryCall: null,

    // Salas de chamada em andamento (Para Join Call em Grupos)
    activeGroupCalls: {} // { [chatId]: { participants: [...userIds], type: 'audio'|'video' } }
};

const slice = createSlice({
    name: 'internalChat',
    initialState,
    reducers: {
        // START LOADING
        startLoading(state) {
            state.isLoading = true;
        },

        // HAS ERROR
        hasError(state, action) {
            state.isLoading = false;
            state.error = action.payload;
        },

        // UI ACTIONS
        setActiveConversation(state, action) {
            const conversationId = action.payload;
            state.ui.activeConversationId = conversationId;

            // Clear unread count for this conversation
            if (conversationId) {
                state.conversations.unreadByConversationId[conversationId] = 0;
            }
        },

        forceReadConversation(state, action) {
            const conversationId = action.payload;
            state.conversations.unreadByConversationId[conversationId] = 0;
            if (state.conversations.byId[conversationId]) {
                state.conversations.byId[conversationId].unreadCount = 0;
            }
        },

        // CONVERSATION ACTIONS
        upsertConversations(state, action) {
            const chats = action.payload;
            chats.forEach(chat => {
                state.conversations.byId[chat._id] = chat;
                if (!state.conversations.allIds.includes(chat._id)) {
                    state.conversations.allIds.push(chat._id);
                }
                state.conversations.unreadByConversationId[chat._id] = chat.unreadCount;

                // Sync presence from participants
                if (chat.participants) {
                    chat.participants.forEach(p => {
                        if (p.userId && typeof p.userId === 'object') {
                            const u = p.userId;
                            state.presence.userStatusById[u._id] = {
                                online: u.online,
                                lastSeenAt: u.lastSeenAt
                            };
                        }
                    });
                }
            });
            state.isLoading = false;
        },

        upsertConversation(state, action) {
            const chat = action.payload;
            state.conversations.byId[chat._id] = chat;
            if (!state.conversations.allIds.includes(chat._id)) {
                state.conversations.allIds.unshift(chat._id);
            }

            // Sync presence from participants
            if (chat.participants) {
                chat.participants.forEach(p => {
                    if (p.userId && typeof p.userId === 'object') {
                        const u = p.userId;
                        state.presence.userStatusById[u._id] = {
                            online: u.online,
                            lastSeenAt: u.lastSeenAt
                        };
                    }
                });
            }
        },

        removeConversation(state, action) {
            const conversationId = action.payload;
            state.conversations.allIds = state.conversations.allIds.filter(id => id !== conversationId);
            delete state.conversations.byId[conversationId];
            delete state.conversations.unreadByConversationId[conversationId];

            if (state.ui.activeConversationId === conversationId) {
                state.ui.activeConversationId = null;
            }
        },

        // MESSAGE ACTIONS
        upsertMessages(state, action) {
            const { conversationId, messages } = action.payload;

            if (!state.messages.idsByConversationId[conversationId]) {
                state.messages.idsByConversationId[conversationId] = [];
            }

            messages.forEach(message => {
                // If message doesn't exist, add it
                if (!state.messages.byId[message._id]) {
                    state.messages.byId[message._id] = message;
                    state.messages.idsByConversationId[conversationId].push(message._id);
                } else {
                    // Update existing
                    state.messages.byId[message._id] = message;
                }
            });
            state.isLoading = false;
        },

        addMessage(state, action) {
            const { conversationId, message, clientId } = action.payload;

            // 1. Deduplication by ID
            if (state.messages.byId[message._id]) return;

            // 2. ClientId check (Optimistic confirmation)
            if (clientId && state.messages.byClientId[clientId]) {
                // const tempId = state.messages.byClientId[clientId];
                // Replace temp message? For now, we assume simple addition logic
                // Ideally we would swap the ID in the list, but let's just add the real one
                // and maybe filter out the temp one if we were tracking it differently.
                // For simplicity in this step: Just verify if it wasn't added already.
            }

            state.messages.byId[message._id] = message;

            if (!state.messages.idsByConversationId[conversationId]) {
                state.messages.idsByConversationId[conversationId] = [];
            }

            state.messages.idsByConversationId[conversationId].push(message._id);

            // Update last message in conversation
            if (state.conversations.byId[conversationId]) {
                state.conversations.byId[conversationId].lastMessage = message;
                state.conversations.byId[conversationId].lastMessageAt = message.timestamp;
                state.conversations.byId[conversationId].updatedAt = message.timestamp;

                // Move conversation to top
                state.conversations.allIds = [
                    conversationId,
                    ...state.conversations.allIds.filter(id => id !== conversationId)
                ];

                // Unread count
                if (state.ui.activeConversationId !== conversationId) {
                    const newCount = (state.conversations.unreadByConversationId[conversationId] || 0) + 1;
                    state.conversations.unreadByConversationId[conversationId] = newCount;
                    if (state.conversations.byId[conversationId]) {
                        state.conversations.byId[conversationId].unreadCount = newCount;
                    }
                }
            }
        },

        // PRESENCE ACTIONS
        updateUserPresence(state, action) {
            const { userId, online, lastSeenAt } = action.payload;
            state.presence.userStatusById[userId] = { online, lastSeenAt };
        },

        // MARK PARTICIPANT AS READ
        markParticipantAsRead(state, action) {
            const { conversationId, userId } = action.payload;
            const chat = state.conversations.byId[conversationId];
            if (chat) {
                const participant = chat.participants.find(p => {
                    const pId = p.userId._id || p.userId;
                    return String(pId) === String(userId);
                });

                if (participant) {
                    participant.lastReadAt = new Date().toISOString();
                    participant.unseenCount = 0;
                }
                if (participant) {
                    participant.lastReadAt = new Date().toISOString();
                    participant.unseenCount = 0;
                }
            }
        },

        // REACTION ACTIONS
        updateMessageReactions(state, action) {
            const { messageId, reactions } = action.payload;
            if (state.messages.byId[messageId]) {
                state.messages.byId[messageId].reactions = reactions;
            }
        },

        // PIN ACTION
        updateChatPin(state, action) {
            const { chatId, pinnedMessage } = action.payload;
            if (state.conversations.byId[chatId]) {
                state.conversations.byId[chatId].pinnedMessage = pinnedMessage;
            }
        },

        // MODE ACTION
        updateChatMode(state, action) {
            const { chatId, mode } = action.payload;
            if (state.conversations.byId[chatId]) {
                state.conversations.byId[chatId].mode = mode;
            }
        },

        // CALL ACTIONS
        setCall(state, action) {
            state.call = { ...state.call, ...action.payload };
        },

        updateCallStatus(state, action) {
            state.call.status = action.payload;
        },

        resetCall(state) {
            state.call = initialState.call;
        },

        // ACTIVE GROUP CALLS
        setActiveGroupCall(state, action) {
            const { chatId, activeCall } = action.payload;
            if (activeCall) {
                state.activeGroupCalls[chatId] = activeCall;
            } else {
                delete state.activeGroupCalls[chatId];
            }
        },

        // SECONDARY CALL (WAITING)
        setSecondaryCall(state, action) {
            state.secondaryCall = action.payload;
        },

        resetSecondaryCall(state) {
            state.secondaryCall = null;
        }
    },
});

// Reducer
export default slice.reducer;

// Actions
export const {
    setActiveConversation,
    upsertConversations,
    upsertConversation,
    removeConversation,
    upsertMessages,
    addMessage,
    updateUserPresence,
    updateMessageReactions,
    updateChatPin,
    updateChatMode,
    markParticipantAsRead,
    setCall,
    updateCallStatus,
    resetCall,
    setActiveGroupCall,
    setSecondaryCall,
    resetSecondaryCall,
    forceReadConversation
} = slice.actions;

// ----------------------------------------------------------------------

export function getChats() {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await axios.get('/api/internal-chat');
            dispatch(slice.actions.upsertConversations(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getMessages(chatId) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await axios.get(`/api/internal-chat/${chatId}/messages`);
            dispatch(slice.actions.upsertMessages({
                conversationId: chatId,
                messages: response.data
            }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function createChat(participantIds) {
    return async (dispatch) => {
        dispatch(slice.actions.startLoading());
        try {
            const response = await axios.post('/api/internal-chat', {
                participantIds,
                type: participantIds.length > 1 ? 'group' : 'private'
            });
            dispatch(slice.actions.upsertConversation(response.data));
            dispatch(slice.actions.setActiveConversation(response.data._id));
            return response.data;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            return null;
        }
    };
}

export function markChatAsRead(chatId) {
    return async (dispatch, getState) => {
        try {
            // First optimistically clear the badges
            dispatch(slice.actions.forceReadConversation(chatId));

            const state = getState();
            // Only fire the API call if it's the currently active chat
            if (state.internalChat.ui.activeConversationId === chatId) {
                await axios.post(`/api/internal-chat/${chatId}/read`);
            }
        } catch (error) {
            console.error('Failed to mark as read', error);
        }
    };
}

// Selectors
export const selectActiveConversationId = (state) => state.internalChat.ui.activeConversationId;
// eslint-disable-next-line arrow-body-style
export const selectConversations = (state) => state.internalChat.conversations.allIds.map(id => state.internalChat.conversations.byId[id]);

export const selectActiveMessages = (state) => {
    const activeId = state.internalChat.ui.activeConversationId;
    if (!activeId) return [];

    const msgIds = state.internalChat.messages.idsByConversationId[activeId] || [];
    return msgIds.map(id => state.internalChat.messages.byId[id]);
};

export const selectActiveGroupCalls = (state) => state.internalChat.activeGroupCalls;

export const selectSecondaryCall = (state) => state.internalChat.secondaryCall;
